import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, ChevronRight, ShoppingBag } from "lucide-react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUS_STYLES = {
  pending:    "bg-yellow-50 text-yellow-700 border-yellow-200",
  paid:       "bg-blue-50 text-blue-700 border-blue-200",
  processing: "bg-purple-50 text-purple-700 border-purple-200",
  shipped:    "bg-indigo-50 text-indigo-700 border-indigo-200",
  delivered:  "bg-green-50 text-green-700 border-green-200",
  cancelled:  "bg-red-50 text-red-700 border-red-200",
};

export default function OrderHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate("/auth", { replace: true }); return; }
    http.get("/store/orders")
      .then(r => setOrders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return (
    <div className="space-y-3 animate-pulse">
      {[1,2,3].map(i => <div key={i} className="h-24 rounded-2xl bg-gray-100" />)}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>My Orders</h1>
        <Link to="/store" className="text-sm font-semibold text-yellow-600 hover:text-yellow-700">← Continue Shopping</Link>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-20">
          <ShoppingBag size={50} className="mx-auto text-gray-200 mb-4" />
          <h2 className="text-xl font-bold text-gray-700 mb-2">No orders yet</h2>
          <p className="text-gray-500 text-sm mb-6">When you place an order, it will appear here.</p>
          <Link to="/store/catalog" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-white" style={{ background: "#0F1E38" }}>
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map(order => {
            const statusClass = STATUS_STYLES[order.status] || STATUS_STYLES.pending;
            const firstImage = order.items?.[0]?.product_image;
            return (
              <Link
                key={order.id}
                to={`/store/order/${order.id}`}
                className="block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-yellow-200 transition-all p-4"
              >
                <div className="flex items-center gap-4">
                  {/* Item preview thumbnails */}
                  <div className="flex -space-x-2 shrink-0">
                    {(order.items || []).slice(0, 3).map((item, i) => (
                      <div key={i} className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 border-2 border-white" style={{ zIndex: 3 - i }}>
                        {item.product_image && <img src={item.product_image} alt="" className="w-full h-full object-cover" />}
                      </div>
                    ))}
                    {(order.items || []).length > 3 && (
                      <div className="w-10 h-10 rounded-lg bg-gray-100 border-2 border-white grid place-items-center text-xs font-bold text-gray-500">+{order.items.length - 3}</div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-bold text-gray-900 text-sm">#{order.order_number || order.id?.slice(-8).toUpperCase()}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${statusClass}`}>
                        {order.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                      <span><Package size={11} className="inline mr-1" />{order.items?.length || 0} item{order.items?.length !== 1 ? "s" : ""}</span>
                      <span>{new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                    </div>
                    {order.tracking_number && (
                      <div className="text-xs text-blue-600 mt-1 font-semibold">Tracking: {order.tracking_number}</div>
                    )}
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="font-bold text-gray-900">${Number(order.total).toFixed(2)}</div>
                    <ChevronRight size={16} className="text-gray-400 ml-auto mt-1" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
